import { memo, useEffect, useRef } from 'react';
import { toastApi, type ToastVariant } from '../Toast/toastApi.js';

export interface NotificationProps {
  readonly show: boolean;
  readonly message: string;
  readonly type: 'success' | 'error' | 'info';
  readonly onClose: () => void;
  readonly autoClose?: boolean;
  readonly duration?: number;
}

function mapToastVariant(type: NotificationProps['type']): ToastVariant {
  return type;
}

/**
 * Toast-backed notification bridge — fires toastApi when visible; no inline DOM.
 * Replaces legacy Notification component (MFE-v3-A-P1-4c).
 */
export const NotificationShell = memo<NotificationProps>(
  ({ show, message, type, onClose, autoClose = true, duration = 5000 }) => {
    const lastFiredRef = useRef<string | null>(null);

    useEffect(() => {
      if (!show || message.length === 0) {
        return undefined;
      }

      const dedupeKey = `${message}:${type}`;
      if (lastFiredRef.current === dedupeKey) {
        return undefined;
      }
      lastFiredRef.current = dedupeKey;

      toastApi({
        message,
        variant: mapToastVariant(type),
        duration: autoClose ? duration : undefined,
      });

      if (!autoClose) {
        onClose();
        return undefined;
      }

      const timer = setTimeout(onClose, duration);
      return (): void => {
        clearTimeout(timer);
      };
    }, [show, message, type, onClose, autoClose, duration]);

    return null;
  },
);
NotificationShell.displayName = 'NotificationShell';

/** @deprecated Use toastApi directly — kept for P3 screen migrations. */
export const Notification = NotificationShell;
